// @ts-nocheck
import { useState } from 'react';
import {
    CreditCard,
    Plus,
    Edit,
    Trash2,
    History,
    Settings,
    ChevronRight,
    Wallet
} from 'lucide-react';
import SubscriptionsSection from './SubscriptionsSection';
import TransactionsPanel from 'components/transactions/Transactions';
import { PaymentMethodErrorBoundary, TransactionErrorBoundary } from './ErrorBoundary';
import { useStripeCards, useRemoveStripeCard, useSubscriptionPlans } from 'core';
import { AddPaymentCard } from './payment-card/add/AddPaymentCard';
import { getCardBrandLabel } from './payment-card/cardsConfig';
import { UpdatePaymentCard } from './payment-card/update/UpdatePaymentCard';
import ConfirmationDialog from "components/ui/confirmationDialog";
import { toast } from "components/ui/toast";
import { PageContainer } from '../ui/page-container';
import PageHeader from 'components/ui/PageHeader';
import { useSearchParams } from 'react-router-dom';
import { SelectField } from "components/ui/select-field";

const PaymentsView = () => {
    /* Tab lives in the URL. Deep links work (/payments?tab=transactions —
       which is where /transactions-history now lands), the back button steps
       through tabs, and a reload keeps you where you were. */
    const [searchParams, setSearchParams] = useSearchParams();
    const [openAddCard, setOpenAddCard] = useState(false)
    const { data: userCards, isFetching: userCardsLoading } = useStripeCards();
    const { mutateAsync: removeCard } = useRemoveStripeCard();
    /* Warms the plans cache so the Browse Plans modal opens without a spinner.
       The plan itself is read by SubscriptionsSection. */
    useSubscriptionPlans();

    // Update Card
    const [selectedCard, setSelectedCard] = useState(null)
    const [confirmUpdateCard, setConfirmUpdateCard] = useState(false)
    const [openUpdateCard, setOpenUpdateCard] = useState(false)

    const [confirmDeleteCard, setConfirmDeleteCard] = useState(false)


    /* One surface for everything to do with money.

       There used to be three: /payments (these tabs), /billing (a second,
       near-unreachable page that printed the subscription record as
       "Email Offer: 1000 / Price: 4.99 / Status: active"), and
       /transactions-history (a third view of the same rows). The account menu
       listed two of them separately. Both of the others now redirect here. */
    const sections = [
        {
            id: 'plan',
            label: 'Plan',
            icon: Settings,
            description: 'Your subscription and what it includes',
        },
        {
            id: 'cards',
            label: 'Payment methods',
            icon: CreditCard,
            description: 'Cards used for billing',
        },
        {
            id: 'transactions',
            label: 'Transactions',
            icon: History,
            description: 'Payment history and invoices',
        },
    ];

    const requestedTab = searchParams.get('tab');
    const activeSection = ['plan', 'cards', 'transactions'].includes(requestedTab)
        ? requestedTab
        : 'plan';

    const handleSectionChange = (sectionId) => {
        const next = new URLSearchParams(searchParams);
        next.set('tab', sectionId);
        setSearchParams(next, { replace: true });
    };

    // Render section navigation — premium underline tab strip
    const renderSectionNavigation = () => (
        <div className="mb-7">
            {/* Desktop: underline tab strip anchored to a bottom border */}
            <div className="hidden md:flex items-end gap-0 border-b border-line dark:border-white/[0.08]">
                {sections.map((section) => {
                    const Icon = section.icon;
                    const isActive = activeSection === section.id;
                    return (
                        <button
                            key={section.id}
                            role="tab"
                            aria-selected={isActive}
                            onClick={() => handleSectionChange(section.id)}
                            className={`group relative flex items-center gap-2 px-5 py-3 text-[13px] font-semibold
                                focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2
                                transition-colors duration-150
                                ${isActive
                                    ? 'text-blue-600 dark:text-blue-400'
                                    : 'text-content-muted dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                                }`}
                        >
                            <Icon
                                className={`w-4 h-4 flex-shrink-0 transition-colors duration-150
                                    ${isActive ? 'text-blue-600 dark:text-blue-400' : 'text-content-subtle dark:text-gray-500 group-hover:text-content-secondary dark:group-hover:text-gray-300'}`}
                                aria-hidden="true"
                            />
                            {section.label}
                            {/* Active underline — sits exactly on the border-b */}
                            <span className={`absolute bottom-0 left-0 right-0 h-[2px] rounded-t-full transition-all duration-200
                                ${isActive ? 'bg-gradient-to-r from-blue-600 to-indigo-500 opacity-100' : 'opacity-0'}`}
                            />
                        </button>
                    );
                })}
            </div>

            {/* Mobile: styled select */}
            <div className="md:hidden">
                <div className="relative w-full">
                    <SelectField
                        value={activeSection}
                        onChange={(e) => handleSectionChange(e.target.value)}
                        className="w-full appearance-none h-10 pl-4 pr-9 rounded-xl text-[13px] font-semibold
                            border border-line dark:border-white/[0.08]
                            bg-surface dark:bg-dark-800
                            text-content dark:text-white
                            focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500
                            transition-colors"
                    >
                        {sections.map((section) => (
                            <option key={section.id} value={section.id}>{section.label}</option>
                        ))}
                    </SelectField>
                    <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-subtle rotate-90 pointer-events-none" />
                </div>
            </div>
        </div>
    );

    // ── Render Billing & Cards ─────────────────────────────────────────────────
    const renderPaymentMethodsSection = () => {
        const BRAND_COLORS = {
            visa: "from-blue-600 to-blue-800",
            mastercard: "from-orange-500 to-red-600",
            amex: "from-gray-600 to-gray-800",
            americanexpress: "from-gray-600 to-gray-800",
            discover: "from-amber-500 to-orange-600",
            dinersclub: "from-slate-500 to-slate-700",
            jcb: "from-emerald-600 to-teal-700",
            unionpay: "from-red-500 to-rose-700",
            default: "from-slate-600 to-slate-800",
        };

        /* Normalised, because the provider is not consistent about how it
           spells a brand — "visa", "Visa", "MasterCard", "amex" and
           "american_express" all turn up. Keyed on letters only, the gradient
           lookup stops falling through to the grey default for anything that
           happens to arrive with a space or an underscore in it. */
        const brandKey = (brand = "") => String(brand).toLowerCase().replace(/[^a-z]/g, "");
        const brandGradient = (brand = "") =>
            BRAND_COLORS[brandKey(brand)] ?? BRAND_COLORS.default;

        return (
            <section className="space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-base font-bold text-content dark:text-white">Billing &amp; Cards</h2>
                        <p className="text-[13px] text-content-muted dark:text-gray-400 mt-0.5">Manage your saved payment methods</p>
                    </div>
                    <button
                        onClick={() => setOpenAddCard(true)}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold
                            text-blue-600 dark:text-blue-400
                            bg-blue-50 dark:bg-blue-500/10
                            border border-blue-100 dark:border-blue-500/20
                            hover:bg-blue-100 dark:hover:bg-blue-500/20
                            transition-colors"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        Add Card
                    </button>
                </div>

                {/* Cards grid */}
                {userCardsLoading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="h-44 rounded-2xl bg-gray-100 dark:bg-dark-700 animate-pulse" />
                        ))}
                    </div>
                ) : userCards && userCards.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {userCards.map((card) => (
                            <div
                                key={card.id}
                                className="rounded-2xl overflow-hidden border border-line dark:border-white/[0.07] shadow-sm hover:shadow-md transition-all duration-200 group"
                            >
                                {/* Gradient face */}
                                <div className={`bg-gradient-to-br ${brandGradient(card.brand)} px-5 pt-5 pb-4`}>
                                    {/* chip + brand */}
                                    <div className="flex items-center justify-between mb-5">
                                        <div className="w-9 h-6 rounded-md bg-yellow-300/80" />
                                        <span className="text-[10px] font-bold text-white/70 uppercase tracking-widest">
                                            {getCardBrandLabel(card.brand)}
                                        </span>
                                    </div>
                                    {/* masked number */}
                                    <p className="text-white text-sm font-mono tracking-[0.2em] mb-4 opacity-90">
                                        •••• &nbsp;•••• &nbsp;•••• &nbsp;{card.last4Digits}
                                    </p>
                                    {/* expiry */}
                                    <div className="flex items-end justify-between">
                                        <div>
                                            <p className="text-[9px] text-white/50 uppercase tracking-wider mb-0.5">Expires</p>
                                            <p className="text-white text-[13px] font-semibold">
                                                {String(card.expMonth).padStart(2, "0")}/{card.expYear}
                                            </p>
                                        </div>
                                        <CreditCard className="w-6 h-6 text-white/20" />
                                    </div>
                                </div>

                                {/* Action bar */}
                                <div className="px-4 py-2.5 flex items-center justify-between bg-surface dark:bg-dark-800 border-t border-line-subtle dark:border-white/[0.06]">
                                    <p className="text-micro font-medium text-content-muted dark:text-gray-400 truncate max-w-[130px]">
                                        {card.name || `${getCardBrandLabel(card.brand)} ••••${card.last4Digits}`}
                                    </p>
                                    <div className="flex items-center gap-0.5">
                                        <button
                                            onClick={() => { setSelectedCard(card); setConfirmUpdateCard(true); }}
                                            className="p-1.5 rounded-lg text-content-subtle hover:text-blue-600 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-colors"
                                            title={`Edit ${getCardBrandLabel(card.brand)} ••••${card.last4Digits}`}
                                        >
                                            <Edit className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            onClick={() => { setSelectedCard(card); setConfirmDeleteCard(true); }}
                                            className="p-1.5 rounded-lg text-content-subtle hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                                            title={`Delete ${getCardBrandLabel(card.brand)} ••••${card.last4Digits}`}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {/* Add new card tile */}
                        <button
                            onClick={() => setOpenAddCard(true)}
                            className="h-full min-h-[10.5rem] rounded-2xl border-2 border-dashed border-line dark:border-white/[0.08]
                                hover:border-blue-400 dark:hover:border-blue-500/50
                                bg-gray-50/50 dark:bg-white/[0.02]
                                hover:bg-blue-50/30 dark:hover:bg-blue-500/[0.05]
                                flex flex-col items-center justify-center gap-2.5
                                transition-all duration-200 group"
                        >
                            <div className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-white/[0.06] group-hover:bg-blue-100 dark:group-hover:bg-blue-500/10 flex items-center justify-center transition-colors">
                                <Plus className="w-4 h-4 text-content-subtle group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
                            </div>
                            <span className="text-[12px] font-semibold text-content-subtle group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">Add new card</span>
                        </button>
                    </div>
                ) : (
                    <div className="rounded-2xl border border-dashed border-line dark:border-white/[0.1] p-10 flex flex-col items-center text-center bg-gray-50/50 dark:bg-white/[0.02]">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-500/10 dark:to-indigo-500/10 border border-blue-100/50 dark:border-blue-500/20 flex items-center justify-center mb-4">
                            <CreditCard className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h3 className="text-[14px] font-bold text-gray-800 dark:text-gray-100 mb-1.5">No payment methods</h3>
                        <p className="text-meta text-content-muted dark:text-gray-400 max-w-xs mb-5 leading-relaxed">Add a card to subscribe to a plan or make payments.</p>
                        <button
                            onClick={() => setOpenAddCard(true)}
                            className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 text-white text-[12px] font-bold px-4 py-2.5 rounded-xl shadow-sm transition-all"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            Add Payment Method
                        </button>
                    </div>
                )}
            </section>
        );
    };

    // Render subscriptions section
    const renderSubscriptionsSection = () => (
        <PaymentMethodErrorBoundary>
            <SubscriptionsSection />
        </PaymentMethodErrorBoundary>
    );

    const renderActiveSection = () => {
        switch (activeSection) {
            case 'cards':
                return renderPaymentMethodsSection();
            case 'transactions':
                /* The standalone /transactions-history implementation, embedded.
                   It was already the better of the two — real KPIs, distinct
                   error and empty states, invoice download — so the duplicate
                   that used to live here was retired rather than kept. */
                return (
                    <TransactionErrorBoundary>
                        <TransactionsPanel embedded />
                    </TransactionErrorBoundary>
                );
            case 'plan':
            default:
                return renderSubscriptionsSection();
        }
    };

    return (
        <PageContainer>
            {/* No stat strip. PaymentsHeader took no props yet was handed five,
                so `totalCards` / `activePlan` / `monthlySpend` / `nextBillingDays`
                were computed and silently discarded — `activePlan` resolved to
                the string "Active Plan", a status wearing a plan's name. The
                Plan tab already shows the plan and the Transactions tab has its
                own stats, so a page-level strip here would only repeat them. */}
            <PageHeader
                icon={<Wallet className="w-5 h-5" />}
                tone="brand"
                title="Billing &amp; Plans"
                subtitle="Your subscription, payment methods and transaction history"
            />

            {renderSectionNavigation()}

            <div className="space-y-6">
                {renderActiveSection()}
            </div>

            <AddPaymentCard showModal={openAddCard} onClose={setOpenAddCard} />

            <ConfirmationDialog
                open={confirmUpdateCard}
                title="Edit Card"
                description="Are you sure want to edit this card ?"
                confirmLabel="Edit Card"
                variant="destructive"
                onConfirm={() => {
                    setConfirmUpdateCard(false);
                    setOpenUpdateCard(true);
                }}
                onCancel={() => setConfirmUpdateCard(false)}
            />

            <UpdatePaymentCard open={openUpdateCard} onClose={setOpenUpdateCard} card={selectedCard} />

            <ConfirmationDialog
                open={confirmDeleteCard}
                title="Delete Card"
                description="Are you sure you want to delete this card?"
                confirmLabel="Delete Card"
                variant="destructive"
                onConfirm={async () => {
                    try {
                        await removeCard({ methodId: selectedCard?.id });
                        setConfirmDeleteCard(false);
                        toast.success("Card deleted successfully!");
                    } catch (err) {
                        toast.error(
                            err?.response?.data?.message ||
                            "Failed to delete card. Please try again."
                        );
                    }
                }}
                onCancel={() => {
                    setConfirmDeleteCard(false);
                }}
            />
        </PageContainer>
    );
};

export default PaymentsView;